import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  Typography,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Container,
  Select,
  MenuItem
} from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

const FeedbackDashboard = () => {
  const [overview, setOverview] = useState(null);
  const [feedbackGraph, setFeedbackGraph] = useState([]);
  const [predictionsGraph, setPredictionsGraph] = useState([]);
  const [dateFilter, setDateFilter] = useState("Week");
  const [predictionFeedBackFilter, setPredictionFeedBackFilter] = useState("feedtotal");
  const [userProfileDetails, setUserProfileDetails] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchUserProfile();
  }, [dateFilter]);

  const fetchDashboardData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/fertilizer/recommendation/feedback/dashboard?date_filter=${dateFilter}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`,
            "Content-Type": "application/json"
          }
        }
      );
      const { data } = response.data;


      // Mapping of original keys to new display names
      const keyMap = {
        "total_predictions": "Total Predictions",
        "total_feedbacks": "Total Feedbacks",
        "feedbacks_approved": "Feedbacks Approved",
        "feedbacks_rejected": "Feedbacks Rejected",
        "feedbacks_pending for approval": "Pending Feedback Approval",
        "last_updated": "Last Updated"
      };
      const keyMapImage = {
        "total_predictions": "/images/Total_predictions.svg",
        "total_feedbacks": "/images/Total_feedbacks.svg",
        "feedbacks_approved": "/images/Feedbacks_approved.svg",
        "feedbacks_rejected": "/images/Feedbacks_rejected.svg",
        "feedbacks_pending for approval": "/images/Pending_feedback_approval.svg",
        "last_updated": "/images/Total_predictions.svg"
      };

      // Transform the object
      const result = Object.keys(data.overview).map(key => ({
        name: keyMap[key] || key,
        value: data.overview[key],
        image: keyMapImage[key] // replace with actual image URLs if needed
      }));

      setOverview(result);

      setFeedbackGraph(data.feedback_graph.data_points);
      setPredictionsGraph(data.predictions_graph.data_points);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const fetchUserProfile = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/api/v1/get/user`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("userToken")}`
          }
        }
      );
      if (
        response.status === 200 &&
        Array.isArray(response.data.data) &&
        response.data.data.length > 0
      ) {
        setUserProfileDetails(response.data.data[0]); // First user profile object
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
    }
  };

  const feedbackOptions = {
    chart: {
      type: "line"
    },
    title: {
      text: "Total Feedback"
    },
    xAxis: {
      categories: feedbackGraph.map(point => point.day)
    },
    yAxis: {
      title: {
        text: "Feedback Count"
      }
    },
    series: [
      {
        name: "Feedback",
        data: feedbackGraph.map(point => point.count)
      }
    ]
  };

  const predictionsOptions = {
    chart: {
      type: "line"
    },
    title: {
      text: "Total Predictions"
    },
    xAxis: {
      categories: predictionsGraph.map(point => point.day)
    },
    yAxis: {
      title: {
        text: "Prediction Count"
      }
    },
    series: [
      {
        name: "Predictions",
        data: predictionsGraph.map(point => point.count)
      }
    ]
  };

  return (
    <Container maxWidth="lg">
      <Grid container>
        <Grid item xs={12}>
          <Grid
            container
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <Grid>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  color: "rgba(0, 0, 0, 0.80)",
                  fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on",
                  fontFamily: "Inter",
                  fontSize: "1.5rem",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "1.125rem"
                }}
              >
                Welcome back,
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  color: "rgba(0, 0, 0, 0.80)",
                  fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on",
                  fontFamily: "Inter",
                  fontSize: "1.5rem",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "1.125rem"
                }}
              >
                {(userProfileDetails && userProfileDetails.name) || "User"} !
              </Typography>
            </Grid>
            <Grid>
              <Typography>{overview && overview[5].value}</Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ marginTop: "1.75rem" }}>
          <Grid
            container
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}
          >
            <Grid>
              <Typography
                variant="h4"
                sx={{
                  color: "rgba(0, 0, 0, 0.80)",
                  fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on",
                  fontFamily: "Inter",
                  fontSize: "1rem",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "1.125rem"
                }}
              >
                Overview
              </Typography>
            </Grid>
            <Grid>
              <Select
                labelId="asset-select-label"
                id="asset-select"
                value={dateFilter}
                onChange={e => setDateFilter(e.target.value)}
                sx={{
                  display: "inline-flex",
                  padding: "0.5rem 0.75rem",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                  borderRadius: "0.5rem",
                  background: "rgba(0, 0, 0, 0.80)",
                  color: "#FFF",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.75rem",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "1.125rem",
                  "& .MuiSelect-icon": {
                    color: "#FFF"
                  },
                  "& fieldset": {
                    border: "none"
                  }
                }}
              >
                <MenuItem value="Today">Today</MenuItem>
                <MenuItem value="Week">Weekly</MenuItem>
                <MenuItem value="Month">Monthly</MenuItem>
                <MenuItem value="All">All</MenuItem>
              </Select>
            </Grid>
          </Grid>
        </Grid>
        {overview && (
          <Grid item xs={12} style={{ marginTop: "3rem", marginBottom: '1.75rem' }}>
            <Grid container spacing={1}>
              {overview
                .filter(key => key.name !== "Last Updated")
                .map(key => (
                  <Grid item xs={12} sm={6} md={4} lg={2.3} key={key}>
                    <div
                      style={{
                        padding: "1.25rem 1.25rem 1.25rem 1.31rem",
                        flexShrink: 0,
                        borderRadius: "0.75rem",
                        border: "1px solid rgba(0, 0, 0, 0.10)",
                        background: "#fff"
                      }}
                    >
                      <Grid container>
                        <Grid item xs={4}>
                          <Typography
                            sx={{
                              color: "rgba(0, 0, 0, 0.80)",
                              fontFeatureSettings:
                                "'ss01' on, 'cv01' on, 'cv11' on",
                              fontFamily: "Inter",
                              fontSize: "0.8125rem",
                              fontStyle: "normal",
                              fontWeight: 400,
                              lineHeight: "1.125rem"
                            }}
                          >
                            {key.name}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          style={{
                            display: "flex",
                            justifyContent: "flex-end",
                            alignItems: "flex-end"
                          }}
                          xs={8}
                        >
                          <img src={key.image}/>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography
                            sx={{
                              color: "#000",
                              fontFeatureSettings:
                                "'ss01' on, 'cv01' on, 'cv11' on",
                              fontFamily: "Inter",
                              fontSize: "1.5rem",
                              fontStyle: "normal",
                              fontWeight: 700,
                              lineHeight: "1.125rem"
                            }}
                          >
                            {key.value}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} style={{ marginTop: "1.13rem" }}>
                          <Typography
                            sx={{
                              color: "rgba(0, 0, 0, 0.60)",
                              fontFeatureSettings:
                                "'ss01' on, 'cv01' on, 'cv11' on",
                              fontFamily: "Inter",
                              fontSize: "0.75rem",
                              fontStyle: "normal",
                              fontWeight: 400,
                              lineHeight: "1.125rem"
                            }}
                          >
                            Last updated:{" "}
                            <span
                              style={{
                                color: "rgba(0, 0, 0, 0.80)"
                              }}
                            >
                              {overview[5].value}
                            </span>
                          </Typography>
                        </Grid>
                      </Grid>
                    </div>
                  </Grid>
                ))}
            </Grid>
          </Grid>
        )}
        <Grid item xs={12} style={{marginBottom: "1.75rem", padding: '1.5rem', flexShrink: 0, borderRadius: '0.75rem', border: '1px solid rgba(0, 0, 0, 0.10)', background: '#FFF'}} >
             <Grid container style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between"
            }}>
             <Grid item>
              <Select
                labelId="asset-select-label"
                id="asset-select"
                value={predictionFeedBackFilter}
                onChange={e => setPredictionFeedBackFilter(e.target.value)}
                sx={{
                  display: "inline-flex",
                  padding: "0.5rem 0.75rem",
                  justifyContent: "center",
                  alignItems: "center",
                  gap: "0.5rem",
                  borderRadius: "0.5rem",
                  background: "rgba(0, 0, 0, 0.80)",
                  color: "#FFF",
                  fontFamily: "Inter, sans-serif",
                  fontSize: "0.75rem",
                  fontStyle: "normal",
                  fontWeight: 500,
                  lineHeight: "1.125rem",
                  "& .MuiSelect-icon": {
                    color: "#FFF"
                  },
                  "& fieldset": {
                    border: "none"
                  }
                }}
              >
                <MenuItem value="feedtotal">Total feedback</MenuItem>
                <MenuItem value="predtotal">Total Prediction</MenuItem>
              </Select>
            </Grid>
                  <Grid item >
                  <Typography
                variant="h4"
                sx={{
                  color: "rgba(0, 0, 0, 0.80)",
                  fontFeatureSettings: "'ss01' on, 'cv01' on, 'cv11' on",
                  fontFamily: "Inter",
                  fontSize: "1.5rem",
                  fontStyle: "normal",
                  fontWeight: 800,
                  lineHeight: "1.125rem"
                }}
              >
                {predictionFeedBackFilter == "feedtotal" ? overview && overview[1].value:overview && overview[0].value}
              </Typography>
                  </Grid>
             <Grid item xs={12}>
                    <HighchartsReact highcharts={Highcharts} options={predictionFeedBackFilter == "feedtotal" ? feedbackOptions : predictionsOptions } />
                  
             </Grid>

             </Grid>
        </Grid>
      </Grid>

    </Container>
  );
};

export default FeedbackDashboard;
